'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import ScoreGauge from './ScoreGauge';
import AchievementBadge from './AchievementBadge';
import CategoryBreakdownChart from './CategoryBreakdownChart';
import Image from 'next/image';

interface AnswerRecord {
  questionId: string;
  selectedOptionId: string;
  isCorrect: boolean;
  category: string;
}

interface ResultsData {
  score: number;
  totalQuestions: number;
  passmark?: number;
  answers: AnswerRecord[];
  completedAt: string;
}

const OFFICIAL_PASS_MARK = 43;
const OFFICIAL_TOTAL = 50;

// Category short labels for chart
const CATEGORY_SHORT: Record<string, string> = {
  'Speed Limits': 'Speed',
  'Road Signs': 'Signs',
  'Safety Margins': 'Safety',
  'Highway Code Rules': 'H.Code',
  'Highway Code Knowledge': 'H.Code',
  'Hazard Awareness': 'Hazards',
  'Vehicle Safety': 'Vehicle',
  'Traffic Signals': 'Signals',
  'Junctions': 'Junctions',
  'Roundabouts': 'Roundabouts',
  'Pedestrian Crossings': 'Crossings',
  'Motorways': 'Motorways',
  'Lane Discipline': 'Lanes',
  'Safe Driving Practices': 'Safe Driving',
  'Emergency Situations': 'Emergency',
  'Weather Conditions': 'Weather',
  'Parking Rules': 'Parking',
};

function buildCategoryData(answers: AnswerRecord[]) {
  const map: Record<string, { correct: number; total: number }> = {};
  for (const a of answers) {
    if (!map[a.category]) map[a.category] = { correct: 0, total: 0 };
    map[a.category].total += 1;
    if (a.isCorrect) map[a.category].correct += 1;
  }
  return Object.entries(map).map(([cat, val]) => ({
    category: CATEGORY_SHORT[cat] ?? cat,
    fullCategory: cat,
    correct: val.correct,
    total: val.total,
    pct: Math.round((val.correct / val.total) * 100),
  }));
}

function getPersonalizedFeedback(score: number, total: number, passmark: number): string {
  const pct = Math.round((score / total) * 100);
  if (score >= total) return 'Perfect score! You have demonstrated exceptional knowledge of the UK Highway Code. You are fully prepared for the official DVSA theory test.';
  if (pct >= 96) return 'Outstanding performance! You are extremely well prepared for the DVSA theory test. Book your test with full confidence.';
  if (score >= passmark) return 'Well done — you have met the official DVSA pass standard. Continue practising to build a larger safety margin before your test.';
  if (score >= passmark - 5) return `You are very close to the pass mark. You need just ${passmark - score} more correct answers. A focused review of your weaker areas should get you there.`;
  if (score >= passmark - 10) return `Good effort — you need ${passmark - score} more correct answers to pass. Review the categories where you scored below 60% and try again.`;
  return `Keep practising! You need ${passmark - score} more correct answers to reach the pass mark. Focus on the highlighted areas for improvement and attempt the quiz again.`;
}

function getRecommendations(categoryData: ReturnType<typeof buildCategoryData>, score: number, total: number, passmark: number) {
  const recs: { category: string; message: string; urgency: 'high' | 'medium' | 'low' }[] = [];
  for (const cat of categoryData) {
    if (cat.pct < 60) {
      recs.push({
        category: cat.fullCategory,
        message: `Revisit ${cat.fullCategory} — you scored ${cat.pct}% (${cat.correct}/${cat.total}). Review the relevant Highway Code section before your test.`,
        urgency: 'high',
      });
    } else if (cat.pct < 86) {
      recs.push({
        category: cat.fullCategory,
        message: `Strengthen your ${cat.fullCategory} knowledge — ${cat.pct}% accuracy (${cat.correct}/${cat.total}). A few more practice rounds will build confidence.`,
        urgency: 'medium',
      });
    }
  }
  if (score >= passmark) {
    recs.push({
      category: 'Overall',
      message: 'You have reached the DVSA pass standard of 43/50. Continue practising to build a larger safety margin before your official test.',
      urgency: 'low',
    });
  }
  return recs;
}

// Demo results for direct navigation to /results-screen
const DEMO_RESULTS: ResultsData = {
  score: 38,
  totalQuestions: 50,
  passmark: 43,
  answers: [
    { questionId: 'q-001', selectedOptionId: 'q-001-b', isCorrect: true, category: 'Road Signs' },
    { questionId: 'q-002', selectedOptionId: 'q-002-b', isCorrect: true, category: 'Road Signs' },
    { questionId: 'q-003', selectedOptionId: 'q-003-b', isCorrect: true, category: 'Road Signs' },
    { questionId: 'q-004', selectedOptionId: 'q-004-c', isCorrect: true, category: 'Road Signs' },
    { questionId: 'q-005', selectedOptionId: 'q-005-a', isCorrect: false, category: 'Road Signs' },
    { questionId: 'q-006', selectedOptionId: 'q-006-b', isCorrect: true, category: 'Speed Limits' },
    { questionId: 'q-007', selectedOptionId: 'q-007-c', isCorrect: true, category: 'Speed Limits' },
    { questionId: 'q-008', selectedOptionId: 'q-008-c', isCorrect: true, category: 'Speed Limits' },
    { questionId: 'q-009', selectedOptionId: 'q-009-c', isCorrect: true, category: 'Speed Limits' },
    { questionId: 'q-010', selectedOptionId: 'q-010-c', isCorrect: true, category: 'Traffic Signals' },
    { questionId: 'q-011', selectedOptionId: 'q-011-b', isCorrect: true, category: 'Traffic Signals' },
    { questionId: 'q-012', selectedOptionId: 'q-012-a', isCorrect: true, category: 'Traffic Signals' },
    { questionId: 'q-013', selectedOptionId: 'q-013-b', isCorrect: true, category: 'Junctions' },
    { questionId: 'q-014', selectedOptionId: 'q-014-b', isCorrect: false, category: 'Junctions' },
    { questionId: 'q-015', selectedOptionId: 'q-015-b', isCorrect: true, category: 'Junctions' },
    { questionId: 'q-016', selectedOptionId: 'q-016-a', isCorrect: true, category: 'Roundabouts' },
    { questionId: 'q-017', selectedOptionId: 'q-017-b', isCorrect: true, category: 'Roundabouts' },
    { questionId: 'q-018', selectedOptionId: 'q-018-a', isCorrect: true, category: 'Roundabouts' },
    { questionId: 'q-019', selectedOptionId: 'q-019-b', isCorrect: true, category: 'Pedestrian Crossings' },
    { questionId: 'q-020', selectedOptionId: 'q-020-c', isCorrect: true, category: 'Pedestrian Crossings' },
    { questionId: 'q-021', selectedOptionId: 'q-021-b', isCorrect: true, category: 'Pedestrian Crossings' },
    { questionId: 'q-022', selectedOptionId: 'q-022-b', isCorrect: true, category: 'Motorways' },
    { questionId: 'q-023', selectedOptionId: 'q-023-b', isCorrect: true, category: 'Motorways' },
    { questionId: 'q-024', selectedOptionId: 'q-024-b', isCorrect: true, category: 'Motorways' },
    { questionId: 'q-025', selectedOptionId: 'q-025-b', isCorrect: true, category: 'Lane Discipline' },
    { questionId: 'q-026', selectedOptionId: 'q-026-a', isCorrect: false, category: 'Lane Discipline' },
    { questionId: 'q-027', selectedOptionId: 'q-027-a', isCorrect: true, category: 'Lane Discipline' },
    { questionId: 'q-028', selectedOptionId: 'q-028-c', isCorrect: true, category: 'Hazard Awareness' },
    { questionId: 'q-029', selectedOptionId: 'q-029-c', isCorrect: true, category: 'Hazard Awareness' },
    { questionId: 'q-030', selectedOptionId: 'q-030-b', isCorrect: true, category: 'Hazard Awareness' },
    { questionId: 'q-031', selectedOptionId: 'q-031-b', isCorrect: true, category: 'Hazard Awareness' },
    { questionId: 'q-032', selectedOptionId: 'q-032-b', isCorrect: true, category: 'Safe Driving Practices' },
    { questionId: 'q-033', selectedOptionId: 'q-033-b', isCorrect: true, category: 'Safe Driving Practices' },
    { questionId: 'q-034', selectedOptionId: 'q-034-a', isCorrect: false, category: 'Safe Driving Practices' },
    { questionId: 'q-035', selectedOptionId: 'q-035-b', isCorrect: true, category: 'Vehicle Safety' },
    { questionId: 'q-036', selectedOptionId: 'q-036-b', isCorrect: true, category: 'Vehicle Safety' },
    { questionId: 'q-037', selectedOptionId: 'q-037-b', isCorrect: true, category: 'Vehicle Safety' },
    { questionId: 'q-038', selectedOptionId: 'q-038-b', isCorrect: true, category: 'Emergency Situations' },
    { questionId: 'q-039', selectedOptionId: 'q-039-a', isCorrect: false, category: 'Emergency Situations' },
    { questionId: 'q-040', selectedOptionId: 'q-040-b', isCorrect: true, category: 'Weather Conditions' },
    { questionId: 'q-041', selectedOptionId: 'q-041-c', isCorrect: true, category: 'Weather Conditions' },
    { questionId: 'q-042', selectedOptionId: 'q-042-b', isCorrect: true, category: 'Parking Rules' },
    { questionId: 'q-043', selectedOptionId: 'q-043-a', isCorrect: true, category: 'Parking Rules' },
    { questionId: 'q-044', selectedOptionId: 'q-044-b', isCorrect: true, category: 'Highway Code Knowledge' },
    { questionId: 'q-045', selectedOptionId: 'q-045-b', isCorrect: true, category: 'Highway Code Knowledge' },
    { questionId: 'q-046', selectedOptionId: 'q-046-a', isCorrect: true, category: 'Highway Code Knowledge' },
    { questionId: 'q-047', selectedOptionId: 'q-047-b', isCorrect: true, category: 'Motorways' },
    { questionId: 'q-048', selectedOptionId: 'q-048-b', isCorrect: true, category: 'Safe Driving Practices' },
    { questionId: 'q-049', selectedOptionId: 'q-049-a', isCorrect: false, category: 'Road Signs' },
    { questionId: 'q-050', selectedOptionId: 'q-050-b', isCorrect: true, category: 'Vehicle Safety' },
  ],
  completedAt: new Date().toISOString(),
};

export default function ResultsEngine() {
  const router = useRouter();
  const [results, setResults] = useState<ResultsData | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [candidateName, setCandidateName] = useState<{ firstName: string; lastName: string } | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('drivetheory_results');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ResultsData;
        setResults(parsed);
      } catch {
        setResults(DEMO_RESULTS);
      }
    } else {
      setResults(DEMO_RESULTS);
    }
    const storedCandidate = sessionStorage.getItem('drivetheory_candidate');
    if (storedCandidate) {
      try {
        setCandidateName(JSON.parse(storedCandidate));
      } catch {
        setCandidateName(null);
      }
    }
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  if (!results) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F9FB' }}>
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-label="Loading results">
            <circle cx="16" cy="16" r="12" stroke="rgba(30,64,175,0.15)" strokeWidth="3" />
            <path d="M16 4a12 12 0 0112 12" stroke="#1E40AF" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <span className="text-sm" style={{ color: '#94A3B8' }}>Loading your results…</span>
        </div>
      </div>
    );
  }

  const { score, totalQuestions, answers, completedAt } = results;
  const passmark = OFFICIAL_PASS_MARK;
  const passed = score >= passmark;
  const percentage = Math.round((score / totalQuestions) * 100);
  const incorrectCount = totalQuestions - score;
  const categoryData = buildCategoryData(answers);
  const recommendations = getRecommendations(categoryData, score, totalQuestions, passmark);
  const personalizedFeedback = getPersonalizedFeedback(score, totalQuestions, passmark);
  const completedDate = new Date(completedAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  function getPerformanceLevel(pct: number): string {
    if (pct === 100) return 'Perfect';
    if (pct >= 96) return 'Outstanding';
    if (pct >= 86) return 'Excellent';
    if (pct >= 70) return 'Good';
    if (pct >= 60) return 'Fair';
    return 'Needs Improvement';
  }

  async function handleDownloadPDF() {
    if (!candidateName) return;
    setPdfLoading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageW = 210;
      const pageH = 297;
      const margin = 18;
      const contentW = pageW - margin * 2;
      let y = 0;

      // ── Header background ──────────────────────────────────────────
      doc.setFillColor(11, 22, 40);
      doc.rect(0, 0, pageW, 52, 'F');

      // Academy name in header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('Galaxy Driving Academy', margin, 22);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(180, 200, 230);
      doc.text('UK Theory Test Practice Assessment Report', margin, 31);

      // Candidate name in header
      const fullName = `${candidateName.firstName} ${candidateName.lastName}`;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(180, 200, 230);
      doc.text(`Candidate: ${fullName}`, margin, 41);

      // Pass/Fail badge in header
      const badgeX = pageW - margin - 36;
      if (passed) {
        doc.setFillColor(5, 150, 105);
        doc.roundedRect(badgeX, 14, 36, 14, 3, 3, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text('✓ PASSED', badgeX + 18, 23, { align: 'center' });
      } else {
        doc.setFillColor(220, 38, 38);
        doc.roundedRect(badgeX, 14, 36, 14, 3, 3, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text('✗ NOT PASSED', badgeX + 18, 23, { align: 'center' });
      }

      y = 62;

      // ── Candidate Name Card ────────────────────────────────────────
      doc.setFillColor(248, 249, 251);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin, y, contentW, 18, 3, 3, 'FD');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('Candidate Name:', margin + 6, y + 7);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(fullName, margin + 6, y + 14);

      y += 26;

      // ── Candidate Summary Card ─────────────────────────────────────
      doc.setFillColor(248, 249, 251);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin, y, contentW, 36, 3, 3, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('CANDIDATE RESULT SUMMARY', margin + 6, y + 8);

      const col1X = margin + 6;
      const col2X = margin + contentW / 2 + 4;
      const rowH = 7;
      let rowY = y + 16;

      const summaryItems = [
        ['Completion Date', completedDate],
        ['Performance Level', getPerformanceLevel(percentage)],
      ];
      const summaryItems2 = [
        ['Assessment Type', 'UK Theory Practice Test'],
        ['Pass Mark', `${passmark}/${totalQuestions} (86%)`],
      ];

      doc.setFontSize(8);
      for (const [label, value] of summaryItems) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(label + ':', col1X, rowY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text(value, col1X + 36, rowY);
        rowY += rowH;
      }
      rowY = y + 16;
      for (const [label, value] of summaryItems2) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(label + ':', col2X, rowY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text(value, col2X + 36, rowY);
        rowY += rowH;
      }

      y += 44;

      // ── Score Stats Row ────────────────────────────────────────────
      const statBoxW = (contentW - 9) / 4;
      const statBoxH = 28;
      const stats = [
        { label: 'Final Score', value: `${score}/${totalQuestions}`, sub: `${percentage}% accuracy`, color: [245, 158, 11] as [number, number, number] },
        { label: 'Correct', value: `${score}`, sub: 'answers', color: [5, 150, 105] as [number, number, number] },
        { label: 'Incorrect', value: `${incorrectCount}`, sub: 'answers', color: [220, 38, 38] as [number, number, number] },
        { label: 'Pass Mark', value: `${passmark}/${totalQuestions}`, sub: passed ? '✓ Met' : `Need ${passmark - score} more`, color: passed ? [5, 150, 105] as [number, number, number] : [220, 38, 38] as [number, number, number] },
      ];

      stats.forEach((stat, i) => {
        const bx = margin + i * (statBoxW + 3);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(stat.color[0], stat.color[1], stat.color[2]);
        doc.setLineWidth(0.5);
        doc.roundedRect(bx, y, statBoxW, statBoxH, 2, 2, 'FD');

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(100, 116, 139);
        doc.text(stat.label.toUpperCase(), bx + statBoxW / 2, y + 7, { align: 'center' });

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(stat.color[0], stat.color[1], stat.color[2]);
        doc.text(stat.value, bx + statBoxW / 2, y + 17, { align: 'center' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(148, 163, 184);
        doc.text(stat.sub, bx + statBoxW / 2, y + 23, { align: 'center' });
      });

      y += statBoxH + 8;

      // ── Performance Level ──────────────────────────────────────────
      const perfLevel = getPerformanceLevel(percentage);
      const perfColor: [number, number, number] = passed ? [5, 150, 105] : [220, 38, 38];
      doc.setFillColor(perfColor[0], perfColor[1], perfColor[2]);
      doc.setGlobalAlpha(0.08);
      doc.roundedRect(margin, y, contentW, 14, 2, 2, 'F');
      doc.setGlobalAlpha(1);
      doc.setDrawColor(perfColor[0], perfColor[1], perfColor[2]);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin, y, contentW, 14, 2, 2, 'D');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(perfColor[0], perfColor[1], perfColor[2]);
      doc.text(`Performance Level: ${perfLevel}`, pageW / 2, y + 9, { align: 'center' });

      y += 22;

      // ── Personalised Feedback ──────────────────────────────────────
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text('Personalised Feedback', margin, y);
      y += 6;

      doc.setFillColor(248, 249, 251);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      const feedbackLines = doc.splitTextToSize(personalizedFeedback, contentW - 12);
      const feedbackH = feedbackLines.length * 5 + 8;
      doc.roundedRect(margin, y, contentW, feedbackH, 2, 2, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text(feedbackLines, margin + 6, y + 7);
      y += feedbackH + 8;

      // ── Category Breakdown ─────────────────────────────────────────
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text('Performance by Topic', margin, y);
      y += 6;

      const catRowH = 7;
      const barMaxW = contentW - 70;
      for (const cat of categoryData) {
        if (y > pageH - 40) {
          doc.addPage();
          y = 20;
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(71, 85, 105);
        doc.text(cat.fullCategory, margin, y + 4);

        doc.setFillColor(241, 245, 249);
        doc.roundedRect(margin + 52, y, barMaxW, 5, 1, 1, 'F');

        const barColor: [number, number, number] = cat.pct >= 86 ? [5, 150, 105] : cat.pct >= 60 ? [217, 119, 6] : [220, 38, 38];
        doc.setFillColor(barColor[0], barColor[1], barColor[2]);
        doc.roundedRect(margin + 52, y, (barMaxW * cat.pct) / 100, 5, 1, 1, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(barColor[0], barColor[1], barColor[2]);
        doc.text(`${cat.pct}%`, margin + 52 + barMaxW + 3, y + 4);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(`${cat.correct}/${cat.total}`, margin + 52 + barMaxW + 14, y + 4);

        y += catRowH;
      }

      y += 6;

      // ── Question Review Grid ───────────────────────────────────────
      if (y > pageH - 60) {
        doc.addPage();
        y = 20;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text('Question Review', margin, y);
      y += 6;

      const cellSize = 8;
      const cellGap = 2;
      const cols = Math.floor(contentW / (cellSize + cellGap));
      answers.forEach((ans, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const cx = margin + col * (cellSize + cellGap);
        const cy = y + row * (cellSize + cellGap);

        if (cy + cellSize > pageH - 30) return;

        const cellColor: [number, number, number] = ans.isCorrect ? [5, 150, 105] : [220, 38, 38];
        doc.setFillColor(cellColor[0], cellColor[1], cellColor[2]);
        doc.setGlobalAlpha(0.12);
        doc.roundedRect(cx, cy, cellSize, cellSize, 1, 1, 'F');
        doc.setGlobalAlpha(1);
        doc.setDrawColor(cellColor[0], cellColor[1], cellColor[2]);
        doc.setLineWidth(0.3);
        doc.roundedRect(cx, cy, cellSize, cellSize, 1, 1, 'D');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5.5);
        doc.setTextColor(cellColor[0], cellColor[1], cellColor[2]);
        doc.text(String(i + 1).padStart(2, '0'), cx + cellSize / 2, cy + cellSize / 2 + 1.5, { align: 'center' });
      });

      const totalRows = Math.ceil(answers.length / cols);
      y += totalRows * (cellSize + cellGap) + 8;

      // ── Footer ─────────────────────────────────────────────────────
      const footerY = pageH - 16;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY - 4, pageW - margin, footerY - 4);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text('Generated by Galaxy Driving Academy', margin, footerY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(148, 163, 184);
      doc.text('This practice assessment is intended for educational purposes only.', pageW / 2, footerY, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(148, 163, 184);
      doc.text(completedDate, pageW - margin, footerY, { align: 'right' });

      // ── Save ───────────────────────────────────────────────────────
      doc.save(`Galaxy-Driving-Academy-Results-${fullName.replace(/ /g, '-')}-${completedDate.replace(/ /g, '-')}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setPdfLoading(false);
    }
  }

  const statsCards = [
    {
      id: 'stat-score',
      label: 'Final Score',
      value: `${score}/${totalQuestions}`,
      sub: `${percentage}% accuracy`,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M10 2l2 5h5l-4 3 1.5 5L10 12l-4.5 3L7 10 3 7h5L10 2z" fill="#F59E0B" />
        </svg>
      ),
      color: 'text-accent-light',
      bg: 'rgba(245,158,11,0.08)',
      border: 'rgba(245,158,11,0.2)',
    },
    {
      id: 'stat-correct',
      label: 'Correct Answers',
      value: `${score}`,
      sub: `out of ${totalQuestions} questions`,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <circle cx="10" cy="10" r="8" stroke="#10B981" strokeWidth="1.5" />
          <path d="M6.5 10l2.5 2.5 4.5-4.5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      color: 'text-success-light',
      bg: 'rgba(16,185,129,0.08)',
      border: 'rgba(16,185,129,0.2)',
    },
    {
      id: 'stat-incorrect',
      label: 'Incorrect Answers',
      value: `${incorrectCount}`,
      sub: `out of ${totalQuestions} questions`,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <circle cx="10" cy="10" r="8" stroke="#EF4444" strokeWidth="1.5" />
          <path d="M7 7l6 6M13 7l-6 6" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ),
      color: incorrectCount === 0 ? 'text-success-light' : 'text-error-light',
      bg: incorrectCount === 0 ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
      border: incorrectCount === 0 ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
    },
    {
      id: 'stat-passmark',
      label: 'Pass Mark',
      value: `${passmark}/${totalQuestions}`,
      sub: passed ? '✓ You met the standard' : `Need ${passmark - score} more`,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M10 2L3 5.5v6c0 4 3 7 7 7.5 4-.5 7-3.5 7-7.5v-6L10 2z" stroke={passed ? '#10B981' : '#EF4444'} strokeWidth="1.5" fill={passed ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)'} />
          {passed && <path d="M7 10l2 2 4-4" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />}
        </svg>
      ),
      color: passed ? 'text-success-light' : 'text-error-light',
      bg: passed ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
      border: passed ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
    },
  ];

  return (
    <div className={`min-h-screen transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`} style={{ background: '#F8F9FB' }}>
      {/* Minimal topbar */}
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid #E2E8F0',
          boxShadow: '0 1px 12px rgba(15,23,42,0.06)',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 h-14 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2" aria-label="Galaxy Driving Academy — Home">
            <div className="relative w-32 h-9 flex-shrink-0">
              <Image
                src="/assets/images/cropped-Untitled-1-scaled-1-1780379093642.webp"
                alt="Galaxy Driving Academy logo"
                fill
                className="object-contain object-left"
                priority
              />
            </div>
          </Link>
          <div className="flex items-center gap-2 text-xs" style={{ color: '#94A3B8' }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" />
              <path d="M6 3.5v3l1.5 1.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
            </svg>
            Completed {completedDate}
          </div>
        </div>
      </header>

      <main className="pt-14 pb-20" aria-label="Quiz results">
        {/* Hero results section */}
        <section
          className="relative overflow-hidden py-14 lg:py-20"
          style={{ background: 'linear-gradient(135deg, #0B1628 0%, #1A2744 50%, #0D1B35 100%)' }}
          aria-labelledby="results-heading"
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-64 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse, rgba(30,64,175,0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />

          <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
            <div className="text-center mb-10">
              <h1
                id="results-heading"
                className="text-section-heading font-bold text-white mb-3 fade-in-up"
                style={{ opacity: 0, animationFillMode: 'forwards' }}
              >
                Your Results
              </h1>

              {/* Candidate name display */}
              {candidateName && (
                <p
                  className="text-sm font-semibold mb-3 fade-in-up"
                  style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: '0.05s', color: 'rgba(147,197,253,0.9)' }}
                >
                  {candidateName.firstName} {candidateName.lastName}
                </p>
              )}

              {/* PASSED / NOT PASSED banner */}
              <div
                className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl mb-4 fade-in-up"
                style={{
                  opacity: 0,
                  animationFillMode: 'forwards',
                  animationDelay: '0.1s',
                  background: passed
                    ? 'rgba(5,150,105,0.15)'
                    : 'rgba(220,38,38,0.15)',
                  border: `1px solid ${passed ? 'rgba(5,150,105,0.4)' : 'rgba(220,38,38,0.4)'}`,
                }}
              >
                {passed ? (
                  <>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <circle cx="10" cy="10" r="9" fill="rgba(5,150,105,0.2)" stroke="#10B981" strokeWidth="1.5" />
                      <path d="M6 10l3 3 5-6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-lg font-bold" style={{ color: '#10B981' }}>✓ PASSED</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <circle cx="10" cy="10" r="9" fill="rgba(220,38,38,0.2)" stroke="#EF4444" strokeWidth="1.5" />
                      <path d="M7 7l6 6M13 7l-6 6" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span className="text-lg font-bold" style={{ color: '#EF4444' }}>✗ NOT PASSED</span>
                  </>
                )}
              </div>

              <p
                className="text-sm max-w-lg mx-auto fade-in-up"
                style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: '0.15s', color: 'rgba(203,213,225,0.85)' }}
              >
                {personalizedFeedback}
              </p>
            </div>

            {/* Score + Badge row */}
            <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-20">
              <div
                className="fade-in-up"
                style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: '0.2s' }}
              >
                <ScoreGauge score={score} total={totalQuestions} passmark={passmark} />
              </div>

              <div
                className="fade-in-up"
                style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: '0.35s' }}
              >
                <AchievementBadge score={score} total={totalQuestions} passmark={passmark} />
              </div>
            </div>
          </div>
        </section>

        {/* Stats cards */}
        <section
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mb-10 mt-8"
          aria-label="Performance statistics"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {statsCards.map((card, i) => (
              <div
                key={card.id}
                className="rounded-2xl p-5 fade-in-up"
                style={{
                  opacity: 0,
                  animationFillMode: 'forwards',
                  animationDelay: `${0.4 + i * 0.08}s`,
                  background: '#FFFFFF',
                  border: `1px solid ${card.border}`,
                  boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>
                    {card.label}
                  </span>
                  {card.icon}
                </div>
                <div className={`font-mono-nums font-bold text-2xl lg:text-3xl ${card.color} mb-1`}>
                  {card.value}
                </div>
                <div className="text-xs" style={{ color: '#94A3B8' }}>{card.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Pass mark indicator bar */}
        <section
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mb-10"
          aria-label="Pass mark indicator"
        >
          <div
            className="rounded-2xl p-6"
            style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', boxShadow: '0 2px 12px rgba(15,23,42,0.06)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-sm" style={{ color: '#0F172A' }}>Score vs Pass Mark</h2>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: passed ? '#059669' : '#3B82F6' }} aria-hidden="true" />
                  <span style={{ color: '#64748B' }}>Your score: {score}/50 ({percentage}%)</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#D97706' }} aria-hidden="true" />
                  <span style={{ color: '#64748B' }}>Pass mark: {passmark}/50 (86%)</span>
                </span>
              </div>
            </div>
            <div className="relative h-6 rounded-full overflow-hidden" style={{ background: '#F1F5F9' }}>
              {/* Pass mark line */}
              <div
                className="absolute top-0 bottom-0 w-0.5 z-10"
                style={{
                  left: `${(passmark / totalQuestions) * 100}%`,
                  background: '#D97706',
                }}
                aria-hidden="true"
              />
              {/* Score bar */}
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{
                  width: `${(score / totalQuestions) * 100}%`,
                  background: passed
                    ? 'linear-gradient(90deg, #059669, #10B981)'
                    : 'linear-gradient(90deg, #1E40AF, #3B82F6)',
                }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs" style={{ color: '#94A3B8' }}>
              <span>0</span>
              <span style={{ color: '#D97706', fontWeight: 600 }}>Pass: {passmark}</span>
              <span>50</span>
            </div>
          </div>
        </section>

        {/* Category breakdown + Recommendations */}
        <section
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mb-10"
          aria-label="Category performance breakdown"
        >
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Category chart */}
            <div
              className="rounded-2xl p-6"
              style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', boxShadow: '0 2px 12px rgba(15,23,42,0.06)' }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-semibold text-base" style={{ color: '#0F172A' }}>Accuracy by Topic</h2>
                <div className="flex items-center gap-3 text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#059669' }} aria-hidden="true" />
                    <span style={{ color: '#94A3B8' }}>≥86%</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#D97706' }} aria-hidden="true" />
                    <span style={{ color: '#94A3B8' }}>60–85%</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#DC2626' }} aria-hidden="true" />
                    <span style={{ color: '#94A3B8' }}>&lt;60%</span>
                  </span>
                </div>
              </div>
              <CategoryBreakdownChart data={categoryData} />

              {/* Category table */}
              <div className="mt-5 flex flex-col gap-2" role="list" aria-label="Category scores">
                {categoryData.map((cat) => (
                  <div key={`cat-row-${cat.category}`} className="flex items-center gap-3" role="listitem">
                    <span className="text-xs w-20 shrink-0 truncate" style={{ color: '#64748B' }}>{cat.category}</span>
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#F1F5F9' }} aria-hidden="true">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          width: `${cat.pct}%`,
                          background: cat.pct >= 86 ? '#059669' : cat.pct >= 60 ? '#D97706' : '#DC2626',
                        }}
                      />
                    </div>
                    <span
                      className="text-xs font-mono-nums font-semibold w-10 text-right shrink-0"
                      style={{ color: cat.pct >= 86 ? '#059669' : cat.pct >= 60 ? '#D97706' : '#DC2626' }}
                    >
                      {cat.pct}%
                    </span>
                    <span className="text-xs w-10 shrink-0" style={{ color: '#94A3B8' }}>
                      {cat.correct}/{cat.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            <div
              className="rounded-2xl p-6"
              style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', boxShadow: '0 2px 12px rgba(15,23,42,0.06)' }}
            >
              <h2 className="font-semibold text-base mb-6" style={{ color: '#0F172A' }}>
                Personalised Recommendations
              </h2>

              {recommendations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 gap-3">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                    <circle cx="20" cy="20" r="18" fill="rgba(5,150,105,0.08)" stroke="#059669" strokeWidth="1.5" />
                    <path d="M12 20l6 6 10-12" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p className="text-sm text-center" style={{ color: '#64748B' }}>
                    Excellent performance across all categories.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4" role="list" aria-label="Improvement recommendations">
                  {recommendations.map((rec, i) => (
                    <div
                      key={`rec-${rec.category}-${i}`}
                      className="flex items-start gap-3 rounded-xl p-4"
                      style={{
                        background: rec.urgency === 'high' ? 'rgba(220,38,38,0.06)' : rec.urgency === 'medium' ? 'rgba(217,119,6,0.06)' : 'rgba(5,150,105,0.06)',
                        border: `1px solid ${rec.urgency === 'high' ? 'rgba(220,38,38,0.18)' : rec.urgency === 'medium' ? 'rgba(217,119,6,0.18)' : 'rgba(5,150,105,0.18)'}`,
                      }}
                      role="listitem"
                    >
                      <div
                        className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                        style={{
                          background: rec.urgency === 'high' ? 'rgba(220,38,38,0.12)' : rec.urgency === 'medium' ? 'rgba(217,119,6,0.12)' : 'rgba(5,150,105,0.12)',
                        }}
                        aria-hidden="true"
                      >
                        {rec.urgency === 'high' ? (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 4v3M6 8.5v.5" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" /></svg>
                        ) : rec.urgency === 'medium' ? (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 6h6" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" /></svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 6l2.5 2.5 4-4" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        )}
                      </div>
                      <div>
                        <span className="text-xs font-semibold block mb-1" style={{ color: rec.urgency === 'high' ? '#DC2626' : rec.urgency === 'medium' ? '#D97706' : '#059669' }}>
                          {rec.category}
                        </span>
                        <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>{rec.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Official DVSA info block */}
              <div
                className="mt-5 rounded-xl p-4 flex items-start gap-3"
                style={{ background: 'rgba(30,64,175,0.06)', border: '1px solid rgba(30,64,175,0.12)' }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 mt-0.5" aria-hidden="true">
                  <circle cx="8" cy="8" r="7" stroke="#1E40AF" strokeWidth="1.2" />
                  <path d="M8 5.5v.5M8 8v3" stroke="#1E40AF" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>
                  The official DVSA theory test requires{' '}
                  <span className="font-semibold" style={{ color: '#1E40AF' }}>43 correct answers out of 50</span>{' '}
                  to pass. This quiz uses the same pass mark as the official test.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Question review summary */}
        <section
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mb-10"
          aria-label="Question by question review"
        >
          <div
            className="rounded-2xl p-6"
            style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', boxShadow: '0 2px 12px rgba(15,23,42,0.06)' }}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-semibold text-base" style={{ color: '#0F172A' }}>Question Review</h2>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded inline-block" style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.4)' }} aria-hidden="true" />
                  <span style={{ color: '#64748B' }}>Correct ({score})</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded inline-block" style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)' }} aria-hidden="true" />
                  <span style={{ color: '#64748B' }}>Incorrect ({incorrectCount})</span>
                </span>
              </div>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2" role="list" aria-label="Answer results per question">
              {answers.map((answer, i) => (
                <div
                  key={`review-${answer.questionId}`}
                  className="flex flex-col items-center gap-1.5"
                  role="listitem"
                  aria-label={`Question ${i + 1}: ${answer.isCorrect ? 'Correct' : 'Incorrect'}`}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center font-mono-nums text-xs font-bold transition-all duration-200 hover:scale-110"
                    style={{
                      background: answer.isCorrect ? 'rgba(5,150,105,0.08)' : 'rgba(220,38,38,0.08)',
                      border: `1.5px solid ${answer.isCorrect ? 'rgba(5,150,105,0.3)' : 'rgba(220,38,38,0.3)'}`,
                      color: answer.isCorrect ? '#059669' : '#DC2626',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="w-4 h-4 rounded-full flex items-center justify-center" aria-hidden="true">
                    {answer.isCorrect ? (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2.5 2.5 3.5-3.5" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M3 3l4 4M7 3l-4 4" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FEATURE 1: Download Results PDF ── */}
        <section
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mb-8"
          aria-label="Download results as PDF"
        >
          <div
            className="rounded-2xl p-6 lg:p-8 flex flex-col sm:flex-row items-center justify-between gap-6"
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              boxShadow: '0 2px 12px rgba(15,23,42,0.06)',
            }}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: 'rgba(30,64,175,0.08)', border: '1px solid rgba(30,64,175,0.15)' }}
                aria-hidden="true"
              >
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M11 3v11M7 10l4 4 4-4" stroke="#1E40AF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 16v1a2 2 0 002 2h10a2 2 0 002-2v-1" stroke="#1E40AF" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </div>
              <div>
                <h2 className="font-bold text-base" style={{ color: '#0F172A' }}>Download Your Results</h2>
                <p className="text-sm mt-0.5" style={{ color: '#64748B' }}>
                  {candidateName
                    ? 'Get a professional PDF report with your full score breakdown, category analysis, and personalised feedback.'
                    : 'Complete the quiz from the start to enable PDF download — candidate name is required.'}
                </p>
              </div>
            </div>
            <button
              onClick={handleDownloadPDF}
              disabled={pdfLoading || !candidateName}
              className="shrink-0 inline-flex items-center gap-2.5 font-semibold text-sm px-7 py-3.5 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{
                background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                color: '#FFFFFF',
                boxShadow: '0 4px 20px rgba(30,64,175,0.35)',
              }}
              aria-label={candidateName ? 'Download results as PDF' : 'PDF download requires candidate name — please start the quiz to enter your name'}
              title={!candidateName ? 'Please start the quiz and enter your name to download the PDF' : undefined}
            >
              {pdfLoading ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                    <path d="M8 2a6 6 0 016 6" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Generating PDF…
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3 12v1a1 1 0 001 1h8a1 1 0 001-1v-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                  Download My Results (PDF)
                </>
              )}
            </button>
          </div>
        </section>

        {/* ── FEATURE 2: Learn with Galaxy Driving Academy CTA ── */}
        <section
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 mb-10"
          aria-label="Learn with Galaxy Driving Academy"
        >
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0B1628 0%, #1A2744 60%, #0D1B35 100%)',
              boxShadow: '0 8px 32px rgba(15,23,42,0.14)',
              border: '1px solid rgba(30,64,175,0.2)',
            }}
          >
            {/* Subtle glow accent */}
            <div className="relative">
              <div
                className="absolute top-0 right-0 w-72 h-48 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at top right, rgba(37,99,235,0.18) 0%, transparent 70%)', filter: 'blur(30px)' }}
                aria-hidden="true"
              />
              <div className="relative z-10 p-8 lg:p-10">
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12">
                  {/* Left: Logo + Text */}
                  <div className="flex-1">
                    {/* Logo */}
                    <div className="relative w-36 h-10 mb-5">
                      <Image
                        src="/assets/images/cropped-Untitled-1-scaled-1-1780379093642.webp"
                        alt="Galaxy Driving Academy logo"
                        fill
                        className="object-contain object-left"
                      />
                    </div>

                    {/* Label pill */}
                    <div
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4"
                      style={{ background: 'rgba(37,99,235,0.2)', color: '#93C5FD', border: '1px solid rgba(37,99,235,0.3)' }}
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                        <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.2" />
                        <path d="M5 3v2.5l1.5 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                      Professional Driving Instruction
                    </div>

                    <h2 className="font-bold text-white mb-2" style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.55rem)', lineHeight: 1.25 }}>
                      Ready to Take the Next Step?
                    </h2>
                    <p className="text-sm leading-relaxed mb-1" style={{ color: 'rgba(148,163,184,0.9)' }}>
                      Learn to Drive with Galaxy Driving Academy
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(203,213,225,0.75)' }}>
                      Continue your driving journey with professional driving lessons and expert guidance from Galaxy Driving Academy.
                    </p>

                    {/* Trust signals */}
                    <div className="flex flex-wrap gap-4 mt-5">
                      {[
                        { icon: '✓', text: 'DVSA Approved Instructors' },
                        { icon: '✓', text: 'Flexible Lesson Times' },
                        { icon: '✓', text: 'High Pass Rate' },
                      ].map((item) => (
                        <span key={item.text} className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(148,163,184,0.85)' }}>
                          <span style={{ color: '#10B981', fontWeight: 700 }}>{item.icon}</span>
                          {item.text}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Right: CTA */}
                  <div className="flex flex-col items-start lg:items-center gap-4 shrink-0">
                    <a
                      href="https://galaxydrivingacademy.co.uk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2.5 font-bold text-sm px-8 py-4 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                      style={{
                        background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                        color: '#FFFFFF',
                        boxShadow: '0 4px 24px rgba(30,64,175,0.5)',
                        whiteSpace: 'nowrap',
                      }}
                      aria-label="Book driving lessons with Galaxy Driving Academy (opens in new tab)"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M5 2v2M11 2v2M2 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      Book Driving Lessons
                    </a>
                    <p className="text-xs text-center" style={{ color: 'rgba(100,116,139,0.8)' }}>
                      Trusted by learner drivers across the UK
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA section */}
        <section
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16"
          aria-label="Next steps"
        >
          <div
            className="rounded-2xl p-8 lg:p-10 text-center"
            style={{
              background: 'linear-gradient(135deg, #0B1628 0%, #1A2744 100%)',
              boxShadow: '0 8px 32px rgba(15,23,42,0.12)',
            }}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-4"
              style={{ background: passed ? 'rgba(5,150,105,0.2)' : 'rgba(239,68,68,0.2)', color: passed ? '#10B981' : '#EF4444', border: `1px solid ${passed ? 'rgba(5,150,105,0.3)' : 'rgba(239,68,68,0.3)'}` }}
            >
              {passed ? '✓ PASSED — 43/50 standard met' : `✗ NOT PASSED — ${passmark - score} more needed`}
            </div>
            <h2 className="font-bold text-xl text-white mb-3">
              {passed ? 'Ready to book your test?' : 'Want to improve your score?'}
            </h2>
            <p className="text-sm max-w-md mx-auto mb-7 leading-relaxed" style={{ color: 'rgba(203,213,225,0.85)' }}>
              {passed
                ? 'You have met the DVSA pass standard of 43/50. Book your theory test at an official DVSA test centre via GOV.UK.'
                : `You scored ${score}/50. You need ${passmark - score} more correct answers to reach the official pass mark of 43/50. Practise again to build your confidence.`}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/quiz-screen"
                className="btn-primary text-sm px-8 py-3.5 rounded-xl"
                style={{ boxShadow: '0 4px 20px rgba(30,64,175,0.45)' }}
                aria-label="Take the quiz again"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M13 8A5 5 0 112.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M2 3v3h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Practice Again
              </Link>
              <Link
                href="/"
                className="btn-secondary-hero text-sm px-8 py-3.5 rounded-xl"
                aria-label="Return to home page"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 7.5L8 3l5 4.5V14H10v-3H6v3H3V7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back to Home
              </Link>
              {passed && (
                <a
                  href="https://www.gov.uk/book-theory-test"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary-hero text-sm px-8 py-3.5 rounded-xl"
                  aria-label="Book your official DVSA theory test on GOV.UK (opens in new tab)"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M10 3h3v3M13 3l-6 6M5 4H3v9h9v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Book on GOV.UK
                </a>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}